// src/lib/lead/actions/reextractCurrentLead.ts
import type { ExtractedLeadV2, Lead, LockedFields } from "@/types/lead";
import type { TextHeuristics } from "@/lib/lead/extraction/textHeuristics";
import {
  mergeLeadFromTranscript,
  type MergeLeadResult,
} from "@/lib/lead/extraction/mergeLeadFromTranscript";

export type ReextractDeps = {
  extract: (text: string) => Promise<ExtractedLeadV2>;
  // textHeuristics is kept around even though the new merge function lives
  // in its own module: callers still rely on `sanitizeCustomerPhone` and
  // future merge-strategy hooks may reuse the heuristics surface.
  textHeuristics: Pick<TextHeuristics, "stripLockedFields" | "mergePreferExistingNonBlank">;
  sanitizeCustomerPhone: (phone: string | null, transcript: string) => string | null;

  getCurrentLead: () => Lead | null;

  setCurrentLead: (l: Lead | null) => void;
  setLeads: React.Dispatch<React.SetStateAction<Lead[]>>;

  currentLeadRef: React.MutableRefObject<Lead | null>;

  /**
   * Called when the merge produces conflicts (manual override differs from
   * transcript). Phase 2 of transcript merge v2 plumbs this to a workspace-
   * level state holder that opens `<LeadMergeConflictModal>`. The lead state
   * is still committed with the conflict-current values preserved, so
   * cancelling the modal is a safe no-op.
   */
  onMergeConflicts?: (args: { leadId: string; mergeResult: MergeLeadResult; source: "reextract" }) => void;
};

export async function reextractCurrentLeadAction(deps: ReextractDeps) {
  const lead = deps.getCurrentLead();
  if (!lead) return;
  if (!lead.transcript?.trim()) return;

  try {
    const text = lead.transcript.trim();
    const freshExtracted = await deps.extract(text);

    const locks = (lead.lockedFields || {}) as LockedFields;
    const baseExtracted = (lead.extracted || {}) as ExtractedLeadV2;

    // Phase 1 of transcript merge v2: `user_overrides` drives clearing-wins.
    // Conflicts from manual overrides are logged but ignored (typed wins);
    // Phase 2 will surface them in `<LeadMergeConflictModal>`.
    const mergeOutcome = mergeLeadFromTranscript({
      baseExtracted,
      userOverrides: lead.user_overrides,
      lockedFields: locks,
      fromTranscript: freshExtracted,
    });
    const mergedExtracted = mergeOutcome.nextExtracted;
    const nextUserOverrides = mergeOutcome.nextUserOverrides;
    if (mergeOutcome.conflicts.length > 0) {
      if (typeof console !== "undefined" && console.debug) {
        console.debug(
          "[reextractCurrentLead] merge conflicts:",
          mergeOutcome.conflicts.map((c) => c.field),
        );
      }
      // Phase 2: surface conflicts to the shell so `<LeadMergeConflictModal>`
      // can resolve them. The lead is still committed below with the typed
      // values preserved, so cancelling the modal is a safe no-op.
      deps.onMergeConflicts?.({ leadId: lead.id, mergeResult: mergeOutcome, source: "reextract" });
    }

    if (!locks.phone) {
      mergedExtracted.phone = deps.sanitizeCustomerPhone(mergedExtracted.phone, text);
    }

    const extractedFullName = [mergedExtracted.first_name, mergedExtracted.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    const updatedLead: Lead = {
      ...lead,
      name: extractedFullName || lead.name,
      transcript: text,
      extracted: mergedExtracted,
      lastUpdated: Date.now(),
      lockedFields: locks,
      user_overrides: nextUserOverrides,
    };

    deps.setCurrentLead(updatedLead);
    deps.currentLeadRef.current = updatedLead;
    deps.setLeads((prev) => prev.map((l) => (l.id === lead.id ? updatedLead : l)));
  } catch (err) {
    console.error("Re-extraction error:", err);
  }
}